import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { NavLink, Row, Table } from 'reactstrap';
import BoldButton from '../../Common/BoldButton';
import { CenteredSpinner } from '../../Common/CenteredSpinner';
import ProductDto from '../../Common/dtos/ProductDto';
import RecipeDto from '../../Common/dtos/RecipeDto';
import KaAlert from '../../Common/KaAlert';
import { KaSelectOption, toKaSelectOptions } from '../../Common/KaSelect';
import OverflowTooltip from '../../Common/OverflowTooltip';
import FilterBadge from '../../Common/Pagination/FilterBadge';
import PaginatedTable from '../../Common/Pagination/PaginatedTable';
import QueryView from '../../Common/QueryView';
import useDisplayMessage from '../../Common/useDisplayMessage';
import FilterInput from '../../FilterInput';
import useGetProducts from '../../Requests/useGetProducts';
import useGetRecipes from '../../Requests/useGetRecipes';
import useGetSitesNonAdmin from '../../Requests/useGetSitesNonAdmin';
import useGetOrderTransactions from '../../../requests/useGetOrderTransactions';
import useGetSharedSites from '../../../requests/useGetSharedSites';
import OrderTransactionDto, { TransactionStatus } from '../../Common/dtos/OrderTransactionDto';
import { ItemType } from '../CreateEditOrderBlendItem';
import { AmountDisplay } from '../../Common/AmountDisplay';
import { Button } from 'react-bootstrap';
import OrderTransactionSearchModal from './OrderTransactionSearchModal';
import { SingleOrderTransactionPage } from './SingleOrderTransactionPage';
import TicketImageView from './TicketImageView';

export type FilterSettings = {
  item: string;
  site: string;
  startDate: string;
  endDate: string;
  partner: string;
}

type SortMode =
  { isHighlighted: boolean, field: SortField, direction: SortDirection }

enum SortField {
  number,
  loadedAt,
}

enum SortDirection {
  ascending,
  descending,
}

const defaultSort: SortMode = ({ isHighlighted: false, field: SortField.loadedAt, direction: SortDirection.descending });

const OrderTransactionsListPage = () => {
  const [showSearchModal, setShowSearchModal] = useState(false);

  const [numberSearch, setNumberSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize] = useState(10);
  const [sortMode, setSortMode] = useState(defaultSort);

  const [filters, setFilters] = useState<FilterSettings>({
    item: '',
    site: '',
    startDate: '',
    endDate: '',
    partner: '',
  });

  const updateFilters = (newFilters: Partial<FilterSettings>) =>
    setFilters({ ...filters, ...newFilters });

  const getSites = useGetSitesNonAdmin();
  const getProducts = useGetProducts();
  const getRecipes = useGetRecipes();
  const getPartnerBusinesses = useGetSharedSites();

  const getOrderTransactions = useGetOrderTransactions({
    page: currentPage,
    pageSize: pageSize,
    filterText: debouncedSearch,
    sortField: SortField[sortMode.field],
    sortDirection: SortDirection[sortMode.direction],
    itemId: filters.item,
    siteId: filters.site,
    startDate: filters.startDate,
    endDate: filters.endDate,
    partnerSiteId: filters.partner,
  }, {
    onSuccess: (paginatedOrderTransactions) => {
      if (!paginatedOrderTransactions) return;
    }
  });

  const displayMessage = useDisplayMessage();
  const { OrderTransactionNumber: initialOrderTransactionNumber, completed: initialCompleted } = useParams();

  const [orderTransactionToView, setOrderTransactionToView] = useState<OrderTransactionDto | undefined>(undefined);
  const [orderTransactionToViewTicket, setOrderTransactionToViewTicket] = useState<OrderTransactionDto | undefined>(undefined);

  useEffect(() => {
    if (initialOrderTransactionNumber) {
      numberSearchChanged(initialOrderTransactionNumber);
    }
  }, [initialOrderTransactionNumber, initialCompleted]);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(numberSearch);
    }, 600);

    return () => clearTimeout(handler);
  }, [numberSearch]);

  const numberSearchChanged = (number: string) => {
    setCurrentPage(0);
    setNumberSearch(number);
  };

  const handlePageChange = (selected: number) => {
    setCurrentPage(selected);
  };

  const handleSortMode = (sortField: SortField) => {
    setSortMode((prev) => {
      const isSameFieldSelected = prev.isHighlighted && prev.field === sortField;

      return {
        isHighlighted: true,
        field: sortField,
        direction: isSameFieldSelected && prev.direction === SortDirection.ascending
          ? SortDirection.descending
          : SortDirection.ascending
      };
    });
  };

  const isSortColumnHighlighted = (sortField: SortField) =>
    sortMode.isHighlighted && sortField === sortMode.field;

  const getOrderTransactionItems = (OrderTransaction: OrderTransactionDto) => 
    OrderTransaction.blends.flatMap(b => b.items.map(i => i.itemName))

  const listOfItems = (products: ProductDto[], recipes: RecipeDto[]) => [
    ...products.map(p => ({
      value: p.id,
      label: p.name,
      type: ItemType.Product
    })),
    ...recipes.map(r => ({
      value: r.id,
      label: r.name,
      type: ItemType.Recipe
    }))
  ];

  const items = listOfItems(getProducts.data ?? [], getRecipes.data ?? []);
  const itemOptions = toKaSelectOptions<KaSelectOption & { type: ItemType }, KaSelectOption & { type: ItemType }>(
    items,
    "value",
    "label",
    { sortByLabel: true }
  );
  const filterItem = items.find((option) => option.value === filters.item);

  const siteOptions = toKaSelectOptions(getSites.data ?? [], "id", "name", { sortByLabel: true });
  const filterSite = siteOptions.find((option) => option.value === filters.site);

  const partnerOptions = (getPartnerBusinesses.data ?? []).map(partner => ({
    value: partner.siteId,
    label: `${partner.siteName} (${partner.businessName})`
  })).sort((a, b) => a.label.localeCompare(b.label));
  const filterPartner = partnerOptions.find((option) => option.value === filters.partner);
  const hasPartnerConnections = partnerOptions.length > 0;

  const sortableColumnHeader = (name: string) => {
    const sortFieldMap: Record<string, SortField> = {
      Number: SortField.number,
      Date: SortField.loadedAt,
    };

    const sortField = sortFieldMap[name] ?? SortField.number;
    const isActive = isSortColumnHighlighted(sortField);
    const { direction } = sortMode;

    return <NavLink
      className="d-inline-flex align-items-center"
      active={isActive}
      onClick={() => handleSortMode(sortField)}>
      {name}
      <i className={`mx-1 ${isActive
        ? direction === SortDirection.ascending
          ? "bi-arrow-down"
          : "bi-arrow-up"
        : "bi-arrow-down-up"
        }`}
        style={{ fontSize: "0.75rem" }}
      />
    </NavLink>
  };

  const refreshPage = () => {
    setOrderTransactionToView(undefined);
    setOrderTransactionToViewTicket(undefined);
    getOrderTransactions.query();
  }

  const handleVoidSuccess = () => {
    displayMessage.success(`Order transaction ${orderTransactionToView?.number} has been voided successfully.`);
    setOrderTransactionToView(undefined);
    refreshPage();
  }

  return <div>
    {orderTransactionToView ? (
      <SingleOrderTransactionPage
        orderTransaction={orderTransactionToView}
        backClicked={refreshPage}
        handleVoidSuccess={handleVoidSuccess}
      />
    ) : orderTransactionToViewTicket ? (
      <TicketImageView
        orderTransaction={orderTransactionToViewTicket}
        backClicked={refreshPage}
      />
    ) : (
      <>
        <Row className="justify-content-between mb-2">
          <h2 className="col-auto ka-blue">Order Transactions</h2>
        </Row>
        <KaAlert displayMessage={displayMessage.message} onClose={displayMessage.clear} />
        <form>
          <div className="row gx-2 gx-md-3 mb-3">
            <div className="col-md-4 mb-2 mb-md-0">
              <FilterInput placeholder='Filter Order Transactions by number or order' value={numberSearch} onFilterTextChange={numberSearchChanged} />
            </div>
            <BoldButton className="col-auto btn-sm px-2 py-0"
              data-testid="show-search-modal"
              onClick={() => setShowSearchModal(true)}>
              <i className="bi-filter" style={{ fontSize: '2rem' }} />
            </BoldButton>
          </div>
        </form>
        <Row>
          <div className="col-auto">
            <FilterBadge
              label={filterItem?.type === ItemType.Product ? "Product" : "Recipe"}
              value={filterItem?.label}
              onClose={() => updateFilters({ item: '' })} />
            <FilterBadge
              label="Site"
              value={filterSite?.label}
              onClose={() => updateFilters({ site: '' })} />
            <FilterBadge
              label="Partner"
              value={filterPartner?.label}
              onClose={() => updateFilters({ partner: '' })} />
            <FilterBadge
              label={"Date"}
              value={
                !!filters.startDate && !!filters.endDate
                  ? `${filters.startDate} - ${filters.endDate}`
                  : ''
              }
              onClose={() => updateFilters({ startDate: '', endDate: '' })} />
          </div>
        </Row>
        <QueryView query={getOrderTransactions}
          renderLoading={() => <div className="mt-8">
            <CenteredSpinner />
          </div>}
          renderData={paginatedOrderTransactions => {
            return <PaginatedTable
              onPageChange={handlePageChange}
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={paginatedOrderTransactions.totalPages}>
              <Table>
                <thead>
                  <tr className="border-bottom border-dark border-1">
                    <th className="col-1" style={{ verticalAlign: 'top' }} />
                    <th className="col-2" style={{ verticalAlign: 'top' }}>
                      {sortableColumnHeader("Transaction")}
                    </th>
                    <th className="col-2" style={{ verticalAlign: 'top' }}>Order</th>
                    <th className="col-3" style={{ verticalAlign: 'top' }}>Products/Recipes</th>
                    <th className="col-2" style={{ verticalAlign: 'top' }}>Order Quantity</th>
                    <th className={hasPartnerConnections ? "col-1" : "col-2"}
                      style={{ verticalAlign: 'top' }}>Dispensed Quantity</th>
                    {hasPartnerConnections && (
                      <th className="col-2" style={{ verticalAlign: 'top' }}>Partner</th>
                    )}
                    <th className="col-3" style={{ verticalAlign: 'top' }}>
                      {sortableColumnHeader("Date")}
                    </th>
                    <th className="col-1" style={{ verticalAlign: 'top' }} />
                  </tr>
                </thead>
                <tbody>
                  {paginatedOrderTransactions.items
                    .map(OrderTransaction =>
                      <tr key={OrderTransaction.id}>
                        <td>
                          <span
                            className={`${OrderTransaction.status === TransactionStatus.Completed ? 'text-primary' : 'text-danger'} fw-semibold`}>
                            {OrderTransaction.status}
                          </span>
                        </td>
                        <td>
                          <p>
                            {OrderTransaction.number}
                          </p>
                        </td>
                        <td>
                          <p>
                            {OrderTransaction.orderNumber}
                          </p>
                        </td>
                        <td>
                          <OverflowTooltip text={getOrderTransactionItems(OrderTransaction).join(", ")} />
                        </td>
                        <td>
                          <AmountDisplay amount={OrderTransaction.totalOrderRequested} hoverId={`requested-${OrderTransaction.id}`} />
                        </td>
                        <td>
                          <AmountDisplay amount={OrderTransaction.totalDispensed} hoverId={`dispensed-${OrderTransaction.id}`} />
                        </td>
                        {hasPartnerConnections && (
                          <td>
                            {OrderTransaction.partnerSiteName && (
                              <>
                                {OrderTransaction.partnerSiteName}
                                {OrderTransaction.partnerBusinessName && (
                                  <span className="text-muted"> ({OrderTransaction.partnerBusinessName})</span>
                                )}
                              </>
                            )}
                          </td>
                        )}
                        <td>
                          <p>
                            {new Date(OrderTransaction.loadedAt).toLocaleDateString()}
                          </p>
                        </td>
                        <td align="right">
                          <div className="dropdown">
                            <Button className="dropdown-toggle btn-ghost-secondary" variant="link" id="actionsMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                              <strong>Actions</strong>
                            </Button>
                            <div className="dropdown-menu" aria-labelledby="actionsMenuButton">
                                <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => setOrderTransactionToView(OrderTransaction)}>View</BoldButton>
                                <BoldButton className="dropdown-item btn-ghost-secondary" onClick={() => setOrderTransactionToViewTicket(OrderTransaction)}>Ticket Image</BoldButton>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                </tbody>
              </Table>
            </PaginatedTable>
          }} />
        <OrderTransactionSearchModal
          show={showSearchModal}
          itemOptions={itemOptions}
          siteOptions={siteOptions}
          partnerOptions={partnerOptions}
          filters={filters}
          updateFilters={updateFilters}
          onClose={() => setShowSearchModal(false)} />
      </>
    )}
  </div>;
}

export { OrderTransactionsListPage };